import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  ArrowLeft, Edit2, ExternalLink, Calendar, CheckCircle, Clock, AlertCircle,
  ChevronRight, PenLine, Smartphone, NotebookPen, CheckSquare, Star, Search, Globe,
  X, Plus, Trash2, Palette, FileText, Briefcase, Sparkles,
  KeyRound,
} from 'lucide-react'
import { supabase } from './supabase'
import ClientNotesPanel from './ClientNotesPanel'
import { useToast } from './ToastContext'
import { AccessTab, OnboardingAnswersCard } from './ClientAccessTab'
import { ROLES, DONE_STATUSES, getStatus, getServiceArea, SERVICE_AREAS } from './constants'
import { format, formatDistanceToNow } from 'date-fns'
import { computeClientAlerts, ALERT_STYLES } from './alerts'

const safeFmt = (dateStr, fmt) => {
  if (!dateStr) return null
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00')
    if (isNaN(d.getTime())) return null
    return format(d, fmt)
  } catch { return null }
}

const BLOG_PHASES = {
  drafting:      { icon: '✍️', label: 'Writing',       color: '#2a6e84' },
  review:        { icon: '👁', label: 'AD Review',     color: '#8c4d78' },
  client_review: { icon: '⏳', label: 'Client Review', color: '#c4962a' },
  publishing:    { icon: '🚀', label: 'Publishing',    color: '#2e6e3a' },
  complete:      { icon: '✅', label: 'Complete',      color: '#2e6e3a' },
}

const SOCIAL_PHASES = {
  drafting:      { icon: '📱', label: 'Building',        color: '#2a6e84' },
  review:        { icon: '👁', label: 'Internal Review', color: '#8c4d78' },
  client_review: { icon: '⏳', label: 'Client Review',   color: '#c4962a' },
  scheduling:    { icon: '📅', label: 'Scheduling',      color: '#2e6e3a' },
  complete:      { icon: '✅', label: 'Complete',        color: '#2e6e3a' },
}

export default function ClientProfileFull({
  clientId, clients, profiles, currentUser,
  onBack, onOpenBlog, onOpenBatch, onEdit, onOpenSeo, onOpenWebsite, onReload, onRunWorkflow, onOpenNote, onOpenTask
}) {
  const toast = useToast()
  const [activeTab, setActiveTab] = useState('overview')
  const [blogs, setBlogs] = useState([])
  const [batches, setBatches] = useState([])
  const [tasks, setTasks] = useState([])
  const [meetingNotes, setMeetingNotes] = useState([])
  const [seoPages, setSeoPages] = useState([])
  const [seoProjects, setSeoProjects] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)

  const client = clients.find(c => c.id === clientId)

  const load = useCallback(async () => {
    if (!clientId) return
    setLoading(true)
    const [{ data: bs }, { data: sbs }, { data: ts }, { data: mn }, { data: sps }, { data: sprs }, { data: docs }] = await Promise.all([
      supabase.from('blog_assignments').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
      supabase.from('social_batches').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
      supabase.from('tasks').select('*, comment_count:task_comments(count)').eq('client_id', clientId).order('created_at', { ascending: false }),
      supabase.from('meeting_notes').select('*').eq('client_id', clientId).order('meeting_date', { ascending: false }),
      supabase.from('seo_pages').select('*').eq('client_id', clientId),
      supabase.from('seo_projects').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
      supabase.from('client_documents').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
    ])
    setBlogs(bs || [])
    setBatches(sbs || [])
    setTasks(ts || [])
    setMeetingNotes(mn || [])
    setSeoPages(sps || [])
    setSeoProjects(sprs || [])
    setDocuments(docs || [])
    setLoading(false)
  }, [clientId])

  useEffect(() => { load() }, [load])

  const audits = useMemo(() => seoProjects.filter(p => p.template_id === 'tpl-quarterly-audit'), [seoProjects])
  const alerts = useMemo(() => client ? computeClientAlerts(client, blogs, batches, audits) : [], [client, blogs, batches, audits])

  if (!client) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>Client not found.</p>
        <button className="btn btn-ghost" onClick={onBack} style={{ marginTop: 16 }}>← Back</button>
      </div>
    )
  }

  const ad = profiles.find(p => p.id === client.account_director_id)
  const am = profiles.find(p => p.id === client.account_manager_id)
  const isArchived = !!client.archived_at

  const openTasks = tasks.filter(t => !DONE_STATUSES.includes(t.status))
  const doneTasks = tasks.filter(t => DONE_STATUSES.includes(t.status))
  const overdueTasks = tasks.filter(t => t.date_due && new Date(t.date_due) < new Date() && !DONE_STATUSES.includes(t.status))

  const TABS = [
    { id: 'overview',  icon: Star,        label: 'Overview' },
    { id: 'brand',     icon: Sparkles,    label: 'Brand Identity' },
    { id: 'plan',      icon: Briefcase,   label: 'Plan' },
    { id: 'documents', icon: FileText,    label: `Docs (${documents.length})` },
    { id: 'access',    icon: KeyRound,    label: 'Access' },
    { id: 'blogs',     icon: PenLine,     label: `Blogs (${blogs.length})` },
    { id: 'social',    icon: Smartphone,  label: `Social (${batches.length})` },
    { id: 'seo',       icon: Search,      label: `SEO (${seoPages.length}p / ${seoProjects.length}pr)` },
    { id: 'website',   icon: Globe,       label: `Website${client?.website_mode ? ` (${getModeShortLabel(client.website_mode)})` : ''}` },
    { id: 'tasks',     icon: CheckSquare, label: `Tasks (${tasks.length})` },
    { id: 'notes',     icon: NotebookPen, label: `Notes (${meetingNotes.length})` },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={14} /> Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--navy)' }}>{client.name}</h1>
            {isArchived && (
              <span style={{ fontSize: 11, background: 'var(--text-muted)', color: '#fff', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>Archived</span>
            )}
            {client.is_onboarding && !client.onboarding_complete && !isArchived && (
              <span style={{ fontSize: 11, background: 'var(--gold)', color: '#fff', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>Onboarding</span>
            )}
            {client.package && (
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--cream-dark)', padding: '2px 10px', borderRadius: 20 }}>{client.package}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            {ad && <span>📋 AD: {ad.full_name}</span>}
            {am && <span>👤 AM: {am.full_name}</span>}
            {client.contact_email && <span>✉️ {client.contact_email}</span>}
            {client.start_date && <span>📅 Started {safeFmt(client.start_date, 'MMM yyyy')}</span>}
          </div>
        </div>
        {onRunWorkflow && !isArchived && (
          <button className="btn btn-gold btn-sm" onClick={() => onRunWorkflow(client)}>⚡ Run Workflow</button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={onEdit}><Edit2 size={13} /> Edit</button>
      </div>

      {/* Alerts banner */}
      {alerts.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {alerts.map(a => {
            const style = ALERT_STYLES[a.level] || ALERT_STYLES.info
            return (
              <div key={a.id}
                onClick={() => {
                  if (a.action === 'open_blogs') setActiveTab('blogs')
                  if (a.action === 'open_social') setActiveTab('social')
                  if (a.action === 'open_seo') setActiveTab('seo')
                }}
                style={{
                  background: style.background, border: style.border, borderRadius: 10,
                  padding: '10px 14px', marginBottom: 8, cursor: 'pointer',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}>
                <span style={{ fontSize: 16 }}>{style.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: style.color, marginBottom: 2 }}>{a.title}</div>
                  <div style={{ fontSize: 12.5, color: style.color, opacity: 0.85 }}>{a.message}</div>
                </div>
                <ChevronRight size={14} style={{ color: style.color, opacity: 0.6, marginTop: 4 }} />
              </div>
            )
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20, overflowX: 'auto' }}>
        {TABS.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} className={`tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <Icon size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
              {t.label}
            </button>
          )
        })}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading client history...</div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <OverviewTab
              client={client}
              tasks={tasks}
              blogs={blogs}
              batches={batches}
              ad={ad}
              am={am}
              openCount={openTasks.length}
              doneCount={doneTasks.length}
              overdueCount={overdueTasks.length}
            />
          )}
          {activeTab === 'brand' && (
            <BrandIdentityTab client={client} onReload={onReload} />
          )}
          {activeTab === 'plan' && (
            <PlanTab client={client} onReload={onReload} />
          )}
          {activeTab === 'documents' && (
            <DocumentsTab client={client} documents={documents} currentUser={currentUser} onReload={() => { load(); onReload && onReload() }} />
          )}
          {activeTab === 'access' && (
            <AccessTab client={client} currentUser={currentUser} />
          )}
          {activeTab === 'blogs' && (
            <BlogsTab blogs={blogs} profiles={profiles} onOpenBlog={onOpenBlog} />
          )}
          {activeTab === 'social' && (
            <SocialTab batches={batches} profiles={profiles} onOpenBatch={onOpenBatch} />
          )}
          {activeTab === 'seo' && (
            <SeoTab pages={seoPages} projects={seoProjects} onOpenSeo={onOpenSeo} clientId={clientId} />
          )}
          {activeTab === 'website' && (
            <WebsiteTab client={client} pages={seoPages} onOpenWebsite={onOpenWebsite} clientId={clientId} />
          )}
          {activeTab === 'tasks' && (
            <TasksTab tasks={tasks} profiles={profiles}
              onOpenTask={onOpenTask ? t => onOpenTask(t, load) : null} />
          )}
          {activeTab === 'notes' && (
            <>
              <ClientNotesPanel clientId={clientId} currentUser={currentUser} profiles={profiles} compact />
              <div style={{ marginTop: 18, marginBottom: 10, fontSize: 11, textTransform: 'uppercase',
                letterSpacing: 0.5, color: 'var(--text-muted)', fontWeight: 600 }}>Meeting notes</div>
              <NotesTab notes={meetingNotes} profiles={profiles} onOpenNote={onOpenNote} />
            </>
          )}
        </>
      )}
    </div>
  )
}

// ─── Overview tab ─────────────────────────────────────────────────────────
function OverviewTab({ client, tasks, blogs, batches, ad, am, openCount, doneCount, overdueCount }) {
  // Active services as small chips
  const serviceChips = [
    client.has_website && { label: 'Website', icon: '🌐' },
    client.has_ads && { label: 'Ads', icon: '📣' },
    client.has_seo && { label: 'SEO', icon: '🔍' },
    client.has_social && { label: `Social${client.social_posts_per_month ? ` (${client.social_posts_per_month}/mo)` : ''}`, icon: '📱' },
    client.has_newsletters && { label: `Newsletters${client.newsletters_per_month ? ` (${client.newsletters_per_month}/mo)` : ''}`, icon: '📧' },
    client.has_blogs && { label: `Blogs${client.blogs_per_month ? ` (${client.blogs_per_month}/mo)` : ''}`, icon: '✍️' },
  ].filter(Boolean)

  return (
    <div>
      {/* Service chips */}
      {serviceChips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {serviceChips.map(s => (
            <span key={s.label} style={{
              fontSize: 12, fontWeight: 600,
              background: 'var(--cream)', color: 'var(--navy)',
              padding: '5px 12px', borderRadius: 50,
              border: '1px solid var(--border-light)',
            }}>
              {s.icon} {s.label}
            </span>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="stats-row" style={{ marginBottom: 20 }}>
        {[
          { label: 'Open Tasks',   value: openCount,    color: 'var(--navy-mid)' },
          { label: 'Completed',    value: doneCount,    color: '#2e6e3a' },
          { label: 'Overdue',      value: overdueCount, color: overdueCount > 0 ? 'var(--red)' : 'var(--text-muted)' },
          { label: 'Blogs',        value: blogs.length, color: 'var(--mauve)' },
          { label: 'Social Batches', value: batches.length, color: 'var(--teal)' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="stat-accent" style={{ background: s.color }} />
            <div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Client basics card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Business Info</span></div>
        <div className="card-body" style={{ display: 'grid', gap: 10 }}>
          {client.website && <Row label="Website" value={<a href={client.website} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>{client.website}</a>} />}
          {client.company_location && <Row label="Location" value={client.company_location} />}
          {client.hosting_platform && <Row label="Hosting" value={client.hosting_platform} />}
          {client.start_date && <Row label="Start Date" value={safeFmt(client.start_date, 'MMMM d, yyyy')} />}
          {client.package_label && <Row label="Package Label" value={client.package_label} />}
          {client.drive_link && <Row label="Drive Folder" value={<a href={client.drive_link} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><ExternalLink size={11} /> Open</a>} />}
        </div>
      </div>

      {/* LMR Team */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">LMR Team</span></div>
        <div className="card-body" style={{ display: 'grid', gap: 10 }}>
          <Row label="Account Director" value={ad?.full_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not assigned</span>} />
          <Row label="Account Manager" value={am?.full_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not assigned</span>} />
        </div>
      </div>

      {/* Primary contact */}
      {(client.primary_contact_name || client.contact_email || client.primary_contact_cell) && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Primary Contact</span></div>
          <div className="card-body" style={{ display: 'grid', gap: 10 }}>
            {client.primary_contact_name && <Row label="Name" value={client.primary_contact_name} />}
            {client.primary_contact_position && <Row label="Position" value={client.primary_contact_position} />}
            {client.contact_email && <Row label="Email" value={<a href={`mailto:${client.contact_email}`} style={{ color: 'var(--teal)' }}>{client.contact_email}</a>} />}
            {client.primary_contact_cell && <Row label="Cell" value={client.primary_contact_cell} />}
          </div>
        </div>
      )}

      {/* Notes */}
      {client.notes && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Internal Notes</span></div>
          <div className="card-body">
            <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{client.notes}</p>
          </div>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 13 }}>
      <div style={{ minWidth: 140, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', paddingTop: 1 }}>{label}</div>
      <div style={{ flex: 1, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  )
}

// ─── Blogs tab ────────────────────────────────────────────────────────────
function BlogsTab({ blogs, profiles, onOpenBlog }) {
  if (blogs.length === 0) {
    return (
      <div className="empty-state">
        <PenLine size={48} className="empty-state-icon" />
        <h3>No blog assignments yet</h3>
        <p>Blog assignments for this client will show up here as they're created.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {blogs.map(blog => {
        const phase = BLOG_PHASES[blog.status] || BLOG_PHASES.drafting
        const copywriter = profiles.find(p => p.id === blog.assigned_to)
        const isComplete = blog.status === 'complete'
        return (
          <div key={blog.id} className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${phase.color}` }}
            onClick={() => onOpenBlog && onOpenBlog(blog)}>
            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{blog.working_h1}</div>
                <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  {copywriter && <span>✍️ {copywriter.full_name}</span>}
                  {blog.primary_keyword && <span>🔑 {blog.primary_keyword}</span>}
                  <span>Created {safeFmt(blog.created_at, 'MMM d, yyyy')}</span>
                  {isComplete && <span style={{ color: '#2e6e3a' }}>· Completed {formatDistanceToNow(new Date(blog.updated_at), { addSuffix: true })}</span>}
                </div>
              </div>
              <span className="status-pill" style={{ background: phase.color + '20', color: phase.color, flexShrink: 0 }}>
                {phase.icon} {phase.label}
              </span>
              <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Social tab ──────────────────────────────────────────────────────────
function SocialTab({ batches, profiles, onOpenBatch }) {
  if (batches.length === 0) {
    return (
      <div className="empty-state">
        <Smartphone size={48} className="empty-state-icon" />
        <h3>No social batches yet</h3>
        <p>Social media batches for this client will show up here as they're created.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {batches.map(batch => {
        const phase = SOCIAL_PHASES[batch.status] || SOCIAL_PHASES.drafting
        const creator = profiles.find(p => p.id === batch.assigned_to)
        const isComplete = batch.status === 'complete'
        return (
          <div key={batch.id} className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${phase.color}` }}
            onClick={() => onOpenBatch && onOpenBatch(batch)}>
            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{batch.month} batch</div>
                <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  {creator && <span>📱 {creator.full_name}</span>}
                  <span>{batch.carousel_count}C · {batch.reel_count}R · {batch.static_count}S</span>
                  <span>Created {safeFmt(batch.created_at, 'MMM d, yyyy')}</span>
                  {isComplete && <span style={{ color: '#2e6e3a' }}>· Completed {formatDistanceToNow(new Date(batch.updated_at), { addSuffix: true })}</span>}
                </div>
              </div>
              <span className="status-pill" style={{ background: phase.color + '20', color: phase.color, flexShrink: 0 }}>
                {phase.icon} {phase.label}
              </span>
              <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Tasks tab ───────────────────────────────────────────────────────────
function TasksTab({ tasks, profiles, onOpenTask }) {
  const [hideCompleted, setHideCompleted] = useState(true)
  const filtered = hideCompleted ? tasks.filter(t => !DONE_STATUSES.includes(t.status)) : tasks

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        <CheckSquare size={48} className="empty-state-icon" />
        <h3>No tasks yet</h3>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, fontSize: 13 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={hideCompleted} onChange={e => setHideCompleted(e.target.checked)} />
          Hide completed
        </label>
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 12 }}>{filtered.length} tasks</span>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="task-table" style={{ width: '100%' }}>
          <thead><tr><th>Task</th><th>Area</th><th>Assigned To</th><th>Due</th><th>Status</th></tr></thead>
          <tbody>
            {filtered.map(t => {
              const sa = getServiceArea(t.service_area)
              const st = getStatus(t.status)
              const assignee = profiles.find(p => p.id === t.assigned_to)
              return (
                <tr key={t.id}
                  onClick={() => onOpenTask && onOpenTask(t)}
                  style={{ cursor: onOpenTask ? 'pointer' : 'default' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {t.is_urgent && <span style={{ fontSize: 10 }}>🔴</span>}
                      {t.blog_assignment_id && <span style={{ fontSize: 10 }}>✍️</span>}
                      {t.social_batch_id && <span style={{ fontSize: 10 }}>📱</span>}
                      <span style={{ fontWeight: 500, fontSize: 13 }}>{t.title}</span>
                    </div>
                  </td>
                  <td>{sa && <span className="badge" style={{ background: sa.color + '18', color: sa.color, fontSize: 11 }}>{sa.label}</span>}</td>
                  <td><span style={{ fontSize: 13 }}>{assignee?.full_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</span></td>
                  <td><span style={{ fontSize: 13 }}>{safeFmt(t.date_due, 'MMM d') || '—'}</span></td>
                  <td>{st && <span className="status-pill" style={{ background: st.bg, color: st.color }}>{st.icon} {st.label}</span>}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── SEO tab ─────────────────────────────────────────────────────────────
function SeoTab({ pages, projects, onOpenSeo, clientId }) {
  const greenPages = pages.filter(p => p.yoast_rating === 'green').length
  const submittedPages = pages.filter(p => p.submitted_to_gsc).length
  const activeProjects = projects.filter(p => p.status !== 'complete' && p.status !== 'archived')
  const completedProjects = projects.filter(p => p.status === 'complete')
  const audits = projects.filter(p => p.template_id === 'tpl-quarterly-audit')
  const lastAudit = audits.filter(a => a.status === 'complete').sort((a, b) => new Date(b.completed_at || b.updated_at) - new Date(a.completed_at || a.updated_at))[0]

  if (pages.length === 0 && projects.length === 0) {
    return (
      <div className="empty-state">
        <Search size={48} className="empty-state-icon" />
        <h3>No SEO work tracked yet</h3>
        <p>Page optimization, projects, and audits for this client will show up here.</p>
        {onOpenSeo && <button className="btn btn-gold" onClick={() => onOpenSeo(clientId)} style={{ marginTop: 16 }}>Open SEO Workspace →</button>}
      </div>
    )
  }

  return (
    <div>
      <div className="stats-row" style={{ marginBottom: 16 }}>
        {[
          { label: 'Pages tracked',    value: pages.length,        color: 'var(--navy-mid)' },
          { label: 'Green-rated',      value: greenPages,          color: '#2e6e3a' },
          { label: 'Submitted to GSC', value: submittedPages,      color: 'var(--teal)' },
          { label: 'Active projects',  value: activeProjects.length, color: 'var(--gold)' },
          { label: 'Done projects',    value: completedProjects.length, color: 'var(--mauve)' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="stat-accent" style={{ background: s.color }} />
            <div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <button className="btn btn-gold btn-sm" onClick={() => onOpenSeo && onOpenSeo(clientId)}>
          Open Full SEO Workspace →
        </button>
      </div>

      {lastAudit && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Last Quarterly Audit</span></div>
          <div className="card-body">
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{lastAudit.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Completed {safeFmt(lastAudit.completed_at || lastAudit.updated_at, 'MMM d, yyyy')}
              {lastAudit.score_start !== null && lastAudit.score_finish !== null && lastAudit.score_start !== undefined && lastAudit.score_finish !== undefined && (
                <> · AHREF: {lastAudit.score_start} → <strong>{lastAudit.score_finish}</strong></>
              )}
            </div>
          </div>
        </div>
      )}

      {activeProjects.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Active SEO Projects</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activeProjects.map(p => (
              <div key={p.id} style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{p.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                  {p.due_date && `Due ${safeFmt(p.due_date, 'MMM d')}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const NOTE_TYPE_META = {
  kickoff:  { icon: '🚀', label: 'Kick-off' },
  weekly:   { icon: '📅', label: 'Weekly' },
  strategy: { icon: '🎯', label: 'Strategy' },
  adhoc:    { icon: '💬', label: 'Ad Hoc' },
}

// Compact title rows, not full note bodies — a client with 40 meetings should
// still be scannable. The body lives in the Meeting Notes editor.
function NotesTab({ notes, profiles, onOpenNote }) {
  if (notes.length === 0) {
    return (
      <div className="empty-state">
        <NotebookPen size={48} className="empty-state-icon" />
        <h3>No meeting notes yet</h3>
        <p>Meeting notes for this client will show up here.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {notes.map(n => {
        const author = profiles.find(p => p.id === n.created_by)
        const mt = NOTE_TYPE_META[n.meeting_type]
        return (
          <div key={n.id} className="card"
            onClick={() => onOpenNote && onOpenNote(n)}
            style={{ cursor: onOpenNote ? 'pointer' : 'default', transition: 'box-shadow 0.15s' }}
            onMouseEnter={e => { if (onOpenNote) e.currentTarget.style.boxShadow = 'var(--shadow-md)' }}
            onMouseLeave={e => { if (onOpenNote) e.currentTarget.style.boxShadow = '' }}>
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 20, width: 28, textAlign: 'center', flexShrink: 0 }}>{mt?.icon || '📝'}</div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {n.title || `Meeting · ${safeFmt(n.meeting_date, 'MMM d, yyyy')}`}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {mt && (
                    <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, background: 'var(--cream-dark)', color: 'var(--text-secondary)' }}>
                      {mt.label}
                    </span>
                  )}
                  <span>{safeFmt(n.meeting_date, 'MMMM d, yyyy')}</span>
                  {author && <span>· {author.full_name}</span>}
                </div>
              </div>

              {n.google_doc_link && (
                <a href={n.google_doc_link} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm"
                  style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <ExternalLink size={12} /> Doc
                </a>
              )}

              {onOpenNote && <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Website tab ─────────────────────────────────────────────────────────
function getModeShortLabel(mode) {
  return { new_build: '🎨 Build', audit_rebuild: '🔍 Rebuild', audit_refresh: '🛠️ Refresh', maintenance_only: '🔧 Maint.' }[mode] || mode
}

function WebsiteTab({ client, pages, onOpenWebsite, clientId }) {
  if (!client?.website_mode) {
    return (
      <div className="empty-state">
        <div style={{ fontSize: 48, marginBottom: 12 }}>🌐</div>
        <h3>No Website Workspace yet</h3>
        <p>This client doesn't have a Website Workspace launched. Open the full workspace to launch one (pick a mode + assign starter task).</p>
        {onOpenWebsite && (
          <button className="btn btn-gold" onClick={() => onOpenWebsite(clientId)} style={{ marginTop: 12 }}>
            🚀 Launch Website Workspace →
          </button>
        )}
      </div>
    )
  }

  const modeLabels = {
    new_build: '🎨 New Build',
    audit_rebuild: '🔍 Audit + Rebuild',
    audit_refresh: '🛠️ Audit + Refresh',
    maintenance_only: '🔧 Maintenance Only',
  }
  const pagesLive = pages.filter(p => p.build_status === 'live').length
  const pagesTotal = pages.length

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ background: 'var(--navy)', color: '#fff', padding: '6px 14px', borderRadius: 50, fontSize: 13, fontWeight: 600 }}>
              {modeLabels[client.website_mode] || client.website_mode}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, fontSize: 13, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pages</div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{pagesLive} / {pagesTotal} live</div>
            </div>
          </div>
          {onOpenWebsite && (
            <button className="btn btn-gold btn-sm" onClick={() => onOpenWebsite(clientId)}>
              Open Website Workspace →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// Brand Identity tab
// ════════════════════════════════════════════════════════════
const BRAND_FIELDS = [
  { key: 'brand_logistics',          label: 'Logistics', hint: 'Insurance, virtual/in-person, other operational details' },
  { key: 'brand_personality',        label: 'Brand Personality', hint: 'If the brand were a person, who would they be? How do they speak?' },
  { key: 'brand_target_audience',    label: 'Target Audience', hint: 'Diagnoses, identities, life experiences they serve' },
  { key: 'brand_feelings',           label: 'Brand Feelings', hint: 'What emotions/feelings should the brand evoke?' },
  { key: 'brand_differentiators',    label: 'Key Differentiators / Value Add', hint: 'What makes them stand out from competitors?' },
  { key: 'brand_promise',            label: 'Brand Promise', hint: 'Their mission, what they commit to delivering' },
  { key: 'brand_services_modalities', label: 'Services / Modalities', hint: 'What the CLIENT offers (couples, families, EMDR, group, etc.)' },
  { key: 'brand_slogan',             label: 'Slogan / Repeated Phrases', hint: 'Key words/phrases to repeat across their brand messaging' },
]

function BrandIdentityTab({ client, onReload }) {
  const [showColorsModal, setShowColorsModal] = useState(false)
  const [showBrandModal, setShowBrandModal] = useState(false)

  const colors = Array.isArray(client.brand_colors) ? client.brand_colors : []
  const fonts = Array.isArray(client.brand_fonts) ? client.brand_fonts : []

  const filledFields = BRAND_FIELDS.filter(f => client[f.key] && client[f.key].trim())

  return (
    <div>
      {/* Colors + Fonts card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="card-title">🎨 Visual Identity</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowColorsModal(true)}><Edit2 size={12} /> Edit</button>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Colors */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Color Palette</div>
              {colors.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {colors.map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid var(--border-light)', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, background: c, border: '1px solid var(--border)' }} />
                      <span style={{ fontFamily: 'monospace' }}>{c}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No colors set yet</div>
              )}
            </div>
            {/* Fonts */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Fonts</div>
              {fonts.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {fonts.map((f, i) => (
                    <div key={i} style={{ fontSize: 13, padding: '4px 0' }}>{i + 1}. {f}</div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No fonts set yet</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Brand identity text card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="card-title">✨ Brand Identity</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowBrandModal(true)}>
            <Edit2 size={12} /> Edit All
          </button>
        </div>
        <div className="card-body">
          {filledFields.length === 0 ? (
            <div className="empty-state" style={{ padding: 30 }}>
              <Sparkles size={32} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No brand identity info yet.</p>
              <button className="btn btn-gold btn-sm" onClick={() => setShowBrandModal(true)} style={{ marginTop: 10 }}>Get started</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 20 }}>
              {BRAND_FIELDS.map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                    {f.label}
                  </div>
                  {client[f.key] && client[f.key].trim() ? (
                    <div style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
                      {client[f.key]}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Not yet captured</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showColorsModal && <VisualIdentityModal client={client} onClose={() => setShowColorsModal(false)} onSaved={() => { setShowColorsModal(false); onReload && onReload() }} />}
      {showBrandModal && <BrandIdentityModal client={client} onClose={() => setShowBrandModal(false)} onSaved={() => { setShowBrandModal(false); onReload && onReload() }} />}
    </div>
  )
}

// Colors + Fonts edit modal
function VisualIdentityModal({ client, onClose, onSaved }) {
  const toast = useToast()
  const [colors, setColors] = useState(Array.isArray(client.brand_colors) ? client.brand_colors : [])
  const [fonts, setFonts] = useState(Array.isArray(client.brand_fonts) ? client.brand_fonts : [])
  const [colorInput, setColorInput] = useState('')
  const [fontInput, setFontInput] = useState('')
  const [saving, setSaving] = useState(false)

  const addColor = () => {
    const hex = colorInput.trim()
    if (!/^#?[0-9a-fA-F]{6}$/.test(hex)) { toast('Enter a 6-digit hex (e.g. #B49C73)', 'error'); return }
    const normalized = hex.startsWith('#') ? hex.toLowerCase() : `#${hex.toLowerCase()}`
    if (colors.length >= 6) { toast('Max 6 colors', 'error'); return }
    setColors([...colors, normalized])
    setColorInput('')
  }
  const removeColor = (i) => setColors(colors.filter((_, idx) => idx !== i))

  const addFont = () => {
    const f = fontInput.trim()
    if (!f) return
    if (fonts.length >= 3) { toast('Max 3 fonts', 'error'); return }
    setFonts([...fonts, f])
    setFontInput('')
  }
  const removeFont = (i) => setFonts(fonts.filter((_, idx) => idx !== i))

  const save = async () => {
    setSaving(true)
    const { error } = await supabase.from('clients').update({ brand_colors: colors, brand_fonts: fonts }).eq('id', client.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Visual identity saved', 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <span className="modal-title">🎨 Edit Visual Identity</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {/* Colors */}
          <div className="form-group">
            <label className="form-label">Color Palette ({colors.length}/6)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {colors.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid var(--border-light)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, background: c, border: '1px solid var(--border)' }} />
                  <span style={{ fontFamily: 'monospace' }}>{c}</span>
                  <button onClick={() => removeColor(i)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, fontSize: 11 }}>×</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={colorInput} onChange={e => setColorInput(e.target.value)} placeholder="#B49C73" className="form-control" style={{ maxWidth: 160 }} onKeyDown={e => e.key === 'Enter' && addColor()} />
              <button className="btn btn-ghost btn-sm" onClick={addColor} disabled={colors.length >= 6}>Add color</button>
            </div>
          </div>

          {/* Fonts */}
          <div className="form-group">
            <label className="form-label">Fonts ({fonts.length}/3)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {fonts.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 8px', background: 'var(--cream)', borderRadius: 4 }}>
                  <span style={{ flex: 1 }}>{i + 1}. {f}</span>
                  <button onClick={() => removeFont(i)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, fontSize: 12 }}>×</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={fontInput} onChange={e => setFontInput(e.target.value)} placeholder="Neulis Sans" className="form-control" style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && addFont()} />
              <button className="btn btn-ghost btn-sm" onClick={addFont} disabled={fonts.length >= 3}>Add font</button>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

// Brand identity text modal — all 8 long-text fields
function BrandIdentityModal({ client, onClose, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState(
    BRAND_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: client[f.key] || '' }), {})
  )
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    const updates = BRAND_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: form[f.key] || null }), {})
    const { error } = await supabase.from('clients').update(updates).eq('id', client.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Brand identity saved', 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <span className="modal-title">✨ Edit Brand Identity</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
          {BRAND_FIELDS.map(f => (
            <div key={f.key} className="form-group">
              <label className="form-label">{f.label}</label>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{f.hint}</div>
              <textarea
                className="form-control"
                rows={3}
                value={form[f.key]}
                onChange={e => set(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save All'}</button>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// Plan tab
// ════════════════════════════════════════════════════════════
const PLAN_FIELDS = [
  { key: 'plan_website',     label: 'Website',     icon: '🌐', flag: 'has_website',     placeholder: 'e.g. "basic updates as needed & SEO optimization"' },
  { key: 'plan_ads',         label: 'Ads',         icon: '📣', flag: 'has_ads',         placeholder: 'e.g. "up to $6K in monthly ad spend"' },
  { key: 'plan_social',      label: 'Social Media', icon: '📱', flag: 'has_social',      placeholder: 'e.g. "15 posts per month, plus reels"' },
  { key: 'plan_newsletters', label: 'Newsletters', icon: '📧', flag: 'has_newsletters', placeholder: 'e.g. "2 newsletters per month via HubSpot"' },
  { key: 'plan_seo_blogs',   label: 'SEO / Blogs', icon: '🔍', flag: 'has_seo',         placeholder: 'e.g. "2 SEO-optimized blogs per month, monthly Yoast optimization"' },
  { key: 'plan_meetings',    label: 'Meetings',    icon: '📅', flag: null,              placeholder: 'e.g. "Bi-weekly check-ins, ads & SEO monitored weekly"' },
]

function PlanTab({ client, onReload }) {
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
        Per-service plan describing what's included for <strong>{client.name}</strong>. Click any card to edit. Cards for services this client doesn't have are dimmed but still editable.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {PLAN_FIELDS.map(f => {
          const isActive = !f.flag || client[f.flag]
          return (
            <PlanCard
              key={f.key}
              client={client}
              field={f}
              isActive={isActive}
              onReload={onReload}
            />
          )
        })}
      </div>
    </div>
  )
}

function PlanCard({ client, field, isActive, onReload }) {
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(client[field.key] || '')
  const [saving, setSaving] = useState(false)

  // Sync if client prop changes
  useEffect(() => { setValue(client[field.key] || '') }, [client, field.key])

  const save = async () => {
    setSaving(true)
    const { error } = await supabase.from('clients').update({ [field.key]: value || null }).eq('id', client.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Plan saved', 'success')
    setEditing(false)
    onReload && onReload()
  }

  const cancel = () => {
    setValue(client[field.key] || '')
    setEditing(false)
  }

  return (
    <div className="card" style={{ opacity: isActive ? 1 : 0.6, transition: 'opacity 150ms' }}>
      <div className="card-body" style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {field.icon} {field.label}
            {!isActive && <span style={{ fontSize: 10, marginLeft: 8, color: 'var(--text-muted)' }}>(not active)</span>}
          </div>
          {!editing && (
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
              <Edit2 size={11} />
            </button>
          )}
        </div>
        {editing ? (
          <>
            <textarea
              className="form-control"
              rows={3}
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={field.placeholder}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={cancel}>Cancel</button>
              <button className="btn btn-gold btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', color: client[field.key] ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: client[field.key] ? 'normal' : 'italic' }}>
            {client[field.key] || field.placeholder}
          </div>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// Documents tab
// ════════════════════════════════════════════════════════════
const COMMON_DOC_LABELS = [
  'Onboarding Info Grab',
  'Ad Dashboard',
  'Content Strategy',
  'SEO Sheet',
  'Website Cover Sheet',
  'Brand Kit',
  'DashThis Dashboard',
  'Passwords / Credentials',
]

function DocumentsTab({ client, documents, currentUser, onReload }) {
  const toast = useToast()
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)

  const del = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}"?`)) return
    const { error } = await supabase.from('client_documents').delete().eq('id', doc.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Document removed', 'success')
    onReload && onReload()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          External docs and dashboards linked to {client.name}. Click any to open in a new tab.
        </div>
        <button className="btn btn-gold btn-sm" onClick={() => setShowAdd(true)}><Plus size={12} /> Add Document</button>
      </div>

      <OnboardingAnswersCard client={client} />

      {documents.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <h3>No documents linked yet</h3>
          <p>Add links to the client's onboarding info, ad dashboard, content strategy doc, etc.</p>
          <button className="btn btn-gold" onClick={() => setShowAdd(true)} style={{ marginTop: 12 }}>
            <Plus size={13} /> Add First Document
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map(d => (
            <div key={d.id} className="card" style={{ padding: 0 }}>
              <div className="card-body" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <FileText size={18} style={{ color: 'var(--teal)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{d.label}</div>
                  {d.doc_type === 'kickoff_deck' ? (
                    // Kickoff decks live inside the app, not at a URL — rendering
                    // this as a link would open a blank tab at the app root.
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Open from the <strong>Kickoff Decks</strong> section in the sidebar
                    </div>
                  ) : (
                    <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--teal)', wordBreak: 'break-all' }}>
                      {d.url} <ExternalLink size={10} style={{ verticalAlign: 'middle', marginLeft: 2 }} />
                    </a>
                  )}
                  {d.notes && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{d.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn-icon" onClick={() => setEditing(d)} title="Edit"><Edit2 size={12} /></button>
                  <button className="btn-icon" onClick={() => del(d)} title="Delete"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <DocumentModal client={client} currentUser={currentUser} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onReload && onReload() }} />}
      {editing && <DocumentModal client={client} doc={editing} currentUser={currentUser} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload && onReload() }} />}
    </div>
  )
}

function DocumentModal({ client, doc, currentUser, onClose, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState({
    label: doc?.label || '',
    url: doc?.url || '',
    notes: doc?.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.label.trim() || !form.url.trim()) { toast('Label and URL are required', 'error'); return }
    setSaving(true)
    if (doc) {
      const { error } = await supabase.from('client_documents').update({ label: form.label, url: form.url, notes: form.notes || null }).eq('id', doc.id)
      if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    } else {
      const { error } = await supabase.from('client_documents').insert({
        client_id: client.id,
        label: form.label,
        url: form.url,
        notes: form.notes || null,
        created_by: currentUser?.id,
      })
      if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    }
    toast(doc ? 'Document updated' : 'Document added', 'success')
    setSaving(false)
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <span className="modal-title">{doc ? 'Edit Document' : 'Add Document'}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Label *</label>
            <input className="form-control" value={form.label} onChange={e => set('label', e.target.value)} placeholder="e.g. Onboarding Info Grab" />
            {!doc && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {COMMON_DOC_LABELS.map(l => (
                  <button key={l} onClick={() => set('label', l)} style={{ fontSize: 11, padding: '3px 8px', background: 'var(--cream)', border: '1px solid var(--border-light)', borderRadius: 50, cursor: 'pointer' }}>
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">URL *</label>
            <input className="form-control" value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://docs.google.com/..." />
          </div>
          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Short context for the team" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving || !form.label.trim() || !form.url.trim()}>{saving ? 'Saving...' : (doc ? 'Save' : 'Add Document')}</button>
        </div>
      </div>
    </div>
  )
}